import { useEffect, useMemo, useState } from 'react';
import {
  Timestamp,
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clipboard,
  Clock3,
  MessageSquareText,
  Pencil,
  Plus,
  Search,
  UserRound,
  X
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';
import { PERMISSIONS } from '../../core/permissions/roles';

const COLUMNS = [
  { key: 'open', label: 'Ouvertes' },
  { key: 'waiting', label: 'En attente' },
  { key: 'blocked', label: 'Bloquées' },
  { key: 'done', label: 'Terminées' }
];

const TYPE_LABELS = {
  sms: 'SMS',
  call: 'Appel',
  contract: 'Contrat',
  finance: 'Finance',
  registration: 'Inscription',
  hr: 'RH',
  schedule: 'Emploi du temps',
  document: 'Document',
  meeting: 'Réunion',
  other: 'Autre'
};

const PRIORITY_LABELS = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  urgent: 'Urgente'
};

function ts(value){
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') return Date.parse(value) || 0;
  return 0;
}

function formatDate(value){
  const ms = ts(value);
  if (!ms) return 'Aucune deadline';
  return new Intl.DateTimeFormat('fr-FR',{
    day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'
  }).format(new Date(ms));
}

function normalizeStatus(task){
  const status = task.status || 'open';
  if (status === 'todo' || status === 'in_progress') return 'open';
  if (status === 'waiting_parent') return 'waiting';
  return status;
}

function deadlineTone(task){
  if (normalizeStatus(task) === 'done') return 'done';
  const due = ts(task.dueDate);
  if (!due) return 'neutral';
  const remaining = due - Date.now();
  if (remaining < 0) return 'overdue';
  if (remaining <= 6 * 60 * 60 * 1000) return 'critical';
  if (remaining <= 24 * 60 * 60 * 1000) return 'soon';
  return 'comfortable';
}

function toInputDate(value){
  const ms = ts(value);
  if (!ms) return '';
  const d = new Date(ms - new Date(ms).getTimezoneOffset()*60000);
  return d.toISOString().slice(0,16);
}

export default function TasksPage(){
  const { user, profile, can } = useAuth();
  const rawRole = profile?.adminRole ?? profile?.role ?? '';
  const roles = (Array.isArray(profile?.adminRoles) ? profile.adminRoles : [rawRole]).map(value => String(value || '').toLowerCase());
  const isGerant = roles.some(role => ['gerant','admin','administration'].includes(role));
  const canManage = can(PERMISSIONS.TASKS_MANAGE);
  const [tasks,setTasks] = useState([]);
  const [administrators,setAdministrators] = useState([]);
  const [assigneeFilter,setAssigneeFilter] = useState('all');
  const [loading,setLoading] = useState(true);
  const [message,setMessage] = useState('');
  const [error,setError] = useState('');
  const [selectedId,setSelectedId] = useState(null);
  const [showCreate,setShowCreate] = useState(false);
  const [showEdit,setShowEdit] = useState(false);
  const [search,setSearch] = useState('');
  const [saving,setSaving] = useState(false);
  const emptyForm = { title:'', description:'', type:'other', priority:'medium', dueDate:'', assignedToName:'', assignedToUid:'' };
  const [form,setForm] = useState(emptyForm);
  const [editForm,setEditForm] = useState(emptyForm);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const tasksSource = isGerant
      ? collection(db,'tasks')
      : query(collection(db,'tasks'), where('assignedToUid','==',user.uid));
    return onSnapshot(tasksSource, snapshot => {
      const rows = snapshot.docs.map(d => ({id:d.id,...d.data()}))
        .filter(t => !t.archivedAt)
        .sort((a,b)=>ts(a.dueDate)-ts(b.dueDate) || ts(b.createdAt)-ts(a.createdAt));
      setTasks(rows);
      setLoading(false);
    }, err => {
      console.error(err);
      setError(`Impossible de charger les tâches : ${err.message}`);
      setLoading(false);
    });
  },[isGerant,user?.uid]);

  useEffect(() => {
    if (!isGerant) return undefined;
    return onSnapshot(collection(db,'users'), snapshot => {
      setAdministrators(snapshot.docs.map(d => ({id:d.id,...d.data()})).filter(item => item.accountType === 'administration' && item.status === 'approved'));
    });
  },[isGerant]);

  const selected = tasks.find(task=>task.id===selectedId) || null;
  const visibleTasks = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return tasks.filter(task => {
      if (!isGerant && task.assignedToUid && task.assignedToUid !== user?.uid) return false;
      if (isGerant && assigneeFilter !== 'all' && task.assignedToUid !== assigneeFilter) return false;
      if (!needle) return true;
      return [task.title, task.description, task.assignedToName, task.parentName, task.sourceType, TYPE_LABELS[task.type]]
        .some(value => String(value || '').toLowerCase().includes(needle));
    });
  },[tasks,search,isGerant,user?.uid,assigneeFilter]);

  const grouped = useMemo(() => Object.fromEntries(
    COLUMNS.map(column => [
      column.key,
      visibleTasks.filter(task => normalizeStatus(task)===column.key)
    ])
  ),[visibleTasks]);

  const openCount = tasks.filter(task=>normalizeStatus(task)!=='done').length;
  const waitingCount = tasks.filter(task=>normalizeStatus(task)==='waiting').length;
  const blockedCount = tasks.filter(task=>normalizeStatus(task)==='blocked').length;
  const doneCount = tasks.filter(task=>normalizeStatus(task)==='done').length;

  function flash(text){
    setMessage(text);
    setTimeout(()=>setMessage(''),1800);
  }

  async function copyDescription(task){
    const content = task.smsMessage || task.description || '';
    await navigator.clipboard.writeText(content);
    flash('Contenu copié.');
  }

  async function setStatus(task,status){
    if (!canManage) return;
    setSaving(true);
    try{
      const payload = {
        status,
        updatedAt:serverTimestamp(),
        updatedByUid:user?.uid || null,
        updatedByName:profile?.fullname || 'Administration'
      };
      if (status==='done') {
        payload.completedAt=serverTimestamp();
        payload.completedByUid=user?.uid || null;
      }
      if (task.subtype==='send_parent_activation' && status==='waiting') {
        payload.smsStatus='sent';
        payload.sentAt=serverTimestamp();
        payload.sentByUid=user?.uid || null;
        payload.sentByName=profile?.fullname || 'Administration';
      }
      const batch = writeBatch(db);
      batch.update(doc(db,'tasks',task.id),payload);
      if (task.subtype==='send_parent_activation' && task.parentId && (status==='waiting' || status==='done')) {
        batch.update(doc(db,'parents',task.parentId), {
          activationStatus:'sms_sent', smsSentAt:serverTimestamp(), smsSentByUid:user?.uid || null, updatedAt:serverTimestamp()
        });
      }
      await batch.commit();
      flash(task.subtype==='send_parent_activation' && (status==='waiting' || status==='done') ? 'SMS et fiche parent mis à jour.' : 'Statut mis à jour.');
    }catch(err){
      console.error(err);
      setError(`Mise à jour impossible : ${err.message}`);
    }finally{
      setSaving(false);
    }
  }


  function openEdit(task){
    setEditForm({
      title:task.title || '',
      description:task.description || task.smsMessage || '',
      type:task.type || 'other',
      priority:task.priority || 'medium',
      dueDate:toInputDate(task.dueDate),
      assignedToUid:task.assignedToUid || '',
      assignedToName:task.assignedToName || ''
    });
    setShowEdit(true);
  }

  async function saveTask(event){
    event.preventDefault();
    if (!selected || !canManage || !editForm.title.trim()) return;
    setSaving(true);
    setError('');
    try {
      const assignee = administrators.find(item => item.id === editForm.assignedToUid);
      await updateDoc(doc(db,'tasks',selected.id), {
        title:editForm.title.trim(),
        description:editForm.description.trim(),
        type:editForm.type,
        priority:editForm.priority,
        dueDate:editForm.dueDate ? Timestamp.fromDate(new Date(editForm.dueDate)) : null,
        assignedToUid:isGerant ? (editForm.assignedToUid || user?.uid || null) : (selected.assignedToUid || user?.uid || null),
        assignedToName:isGerant ? (assignee?.fullname || assignee?.fullName || assignee?.email || profile?.fullname || 'Administration') : (selected.assignedToName || profile?.fullname || 'Administration'),
        updatedAt:serverTimestamp(),
        updatedByUid:user?.uid || null,
        updatedByName:profile?.fullname || 'Administration'
      });
      setShowEdit(false);
      flash('Tâche modifiée.');
    } catch(err) {
      console.error(err);
      setError(`Modification impossible : ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function createTask(event){
    event.preventDefault();
    if (!canManage || !form.title.trim()) return;
    setSaving(true);
    setError('');
    try{
      await addDoc(collection(db,'tasks'),{
        title:form.title.trim(),
        description:form.description.trim(),
        type:form.type,
        priority:form.priority,
        status:'open',
        dueDate:form.dueDate ? Timestamp.fromDate(new Date(form.dueDate)) : null,
        assignedToUid:isGerant ? (form.assignedToUid || user?.uid || null) : (user?.uid || null),
        assignedToName:isGerant ? (administrators.find(item=>item.id===form.assignedToUid)?.fullname || form.assignedToName.trim() || profile?.fullname || 'Administration') : (profile?.fullname || 'Administration'),
        sourceType:'manual',
        createdAt:serverTimestamp(),
        createdByUid:user?.uid || null,
        createdByName:profile?.fullname || 'Administration'
      });
      setShowCreate(false);
      setForm(emptyForm);
      flash('Tâche créée.');
    }catch(err){
      console.error(err);
      setError(`Création impossible : ${err.message}`);
    }finally{
      setSaving(false);
    }
  }

  return <>
    <PageHeader
      eyebrow="MODULE"
      title="Tâches"
      description="Organisez les actions, deadlines et suivis de tous les modules."
    />

    {error && <div className="dashboard-alert"><div><strong>Une erreur est survenue.</strong><span>{error}</span></div></div>}
    {message && <div className="success-note task-toast"><CheckCircle2 size={17}/>{message}</div>}

    <section className="task-toolbar panel">
      <label className="task-search"><Search size={18}/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher une tâche, un responsable, une source…"/></label>
      {isGerant && <select className="task-assignee-filter" value={assigneeFilter} onChange={e=>setAssigneeFilter(e.target.value)}><option value="all">Tous les responsables</option>{administrators.map(item=><option key={item.id} value={item.id}>{item.fullname || item.fullName || item.email || item.id}</option>)}</select>}
      {canManage && <button className="button button-primary" onClick={()=>setShowCreate(true)}><Plus size={17}/> Nouvelle tâche</button>}
    </section>

    <section className="task-kpis">
      <div className="panel task-kpi"><span>Total ouvert</span><strong>{openCount}</strong></div>
      <div className="panel task-kpi"><span>En attente</span><strong>{waitingCount}</strong></div>
      <div className="panel task-kpi"><span>Bloqué</span><strong>{blockedCount}</strong></div>
      <div className="panel task-kpi"><span>Fait</span><strong>{doneCount}</strong></div>
    </section>

    <section className="kanban-board">
      {COLUMNS.map(column => <div className="kanban-column" key={column.key}>
        <header><span>{column.label}</span><strong>{grouped[column.key]?.length || 0}</strong></header>
        <div className="kanban-stack">
          {loading && column.key==='open' && <div className="compact-empty"><Clock3 size={20}/>Chargement…</div>}
          {!loading && (grouped[column.key]?.length || 0)===0 && <div className="kanban-empty">Aucune tâche</div>}
          {(grouped[column.key] || []).map(task => <button className={`task-card compact ${deadlineTone(task)}`} key={task.id} onClick={()=>setSelectedId(task.id)}>
            <div className="task-card-topline">
              <span className="task-type">{TYPE_LABELS[task.type] || 'Tâche'}</span>
              <span className={`priority-chip priority-${task.priority || 'medium'}`}>{PRIORITY_LABELS[task.priority || 'medium']}</span>
            </div>
            <h3>{task.title}</h3>
            <div className="task-deadline"><CalendarClock size={15}/><span>{formatDate(task.dueDate)}</span></div>
            <div className="task-meta"><UserRound size={15}/><span>{task.assignedToName || 'Non affectée'}</span></div>
          </button>)}
        </div>
      </div>)}
    </section>

    {showCreate && <div className="modal-layer">
      <form className="task-create-modal panel" onSubmit={createTask}>
        <header><div><p className="eyebrow">NOUVELLE TÂCHE</p><h2>Créer une tâche</h2></div><button type="button" className="drawer-close" onClick={()=>setShowCreate(false)}><X size={20}/></button></header>
        <div className="task-create-body">
          <label>Titre<input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} required/></label>
          <div className="form-two-columns">
            <label>Type<select value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>{Object.entries(TYPE_LABELS).map(([value,label])=><option value={value} key={value}>{label}</option>)}</select></label>
            <label>Priorité<select value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>{Object.entries(PRIORITY_LABELS).map(([value,label])=><option value={value} key={value}>{label}</option>)}</select></label>
          </div>
          <label>Deadline<input type="datetime-local" value={form.dueDate} onChange={e=>setForm({...form,dueDate:e.target.value})}/></label>
          {isGerant ? <label>Responsable<select value={form.assignedToUid} onChange={e=>setForm({...form,assignedToUid:e.target.value})}><option value="">Moi-même</option>{administrators.map(item=><option key={item.id} value={item.id}>{item.fullname || item.fullName || item.email || item.id}</option>)}</select></label> : <label>Responsable<input value={profile?.fullname || 'Administration'} disabled/></label>}
          <label>Description<textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Instructions, contenu du message, détails…"/></label>
          <div className="family-form-footer"><button type="button" className="button button-secondary" onClick={()=>setShowCreate(false)}>Annuler</button><button className="button button-primary" disabled={saving}>Créer la tâche</button></div>
        </div>
      </form>
    </div>}

    {showEdit && selected && <div className="modal-layer">
      <form className="task-create-modal panel" onSubmit={saveTask}>
        <header><div><p className="eyebrow">MODIFIER LA TÂCHE</p><h2>{selected.title}</h2></div><button type="button" className="drawer-close" onClick={()=>setShowEdit(false)}><X size={20}/></button></header>
        <div className="task-create-body">
          <label>Titre<input value={editForm.title} onChange={e=>setEditForm({...editForm,title:e.target.value})} required/></label>
          <div className="form-two-columns">
            <label>Type<select value={editForm.type} onChange={e=>setEditForm({...editForm,type:e.target.value})}>{Object.entries(TYPE_LABELS).map(([value,label])=><option value={value} key={value}>{label}</option>)}</select></label>
            <label>Priorité<select value={editForm.priority} onChange={e=>setEditForm({...editForm,priority:e.target.value})}>{Object.entries(PRIORITY_LABELS).map(([value,label])=><option value={value} key={value}>{label}</option>)}</select></label>
          </div>
          <label>Deadline<input type="datetime-local" value={editForm.dueDate} onChange={e=>setEditForm({...editForm,dueDate:e.target.value})}/></label>
          {isGerant && <label>Responsable<select value={editForm.assignedToUid} onChange={e=>setEditForm({...editForm,assignedToUid:e.target.value})}><option value={user?.uid || ''}>Moi-même</option>{administrators.map(item=><option key={item.id} value={item.id}>{item.fullname || item.fullName || item.email || item.id}</option>)}</select></label>}
          <label>Description<textarea value={editForm.description} onChange={e=>setEditForm({...editForm,description:e.target.value})}/></label>
          <div className="family-form-footer"><button type="button" className="button button-secondary" onClick={()=>setShowEdit(false)}>Annuler</button><button className="button button-primary" disabled={saving}>Enregistrer</button></div>
        </div>
      </form>
    </div>}

    {selected && <div className="drawer-backdrop" onMouseDown={e=>e.target===e.currentTarget&&setSelectedId(null)}>
      <aside className="registration-drawer task-drawer">
        <header className="drawer-header"><div><p className="eyebrow">TÂCHE</p><h2>{selected.title}</h2><div className="task-drawer-badges"><span className="task-type">{TYPE_LABELS[selected.type] || 'Tâche'}</span><span className={`priority-chip priority-${selected.priority || 'medium'}`}>{PRIORITY_LABELS[selected.priority || 'medium']}</span></div></div><div className="drawer-header-actions">{canManage && <button className="button button-secondary" onClick={()=>openEdit(selected)}><Pencil size={16}/> Modifier</button>}<button className="drawer-close" onClick={()=>setSelectedId(null)}><X size={20}/></button></div></header>
        <div className="drawer-scroll">
          <section className="detail-card"><h3>Informations</h3><dl className="detail-grid"><div><dt>Statut</dt><dd>{COLUMNS.find(c=>c.key===normalizeStatus(selected))?.label || normalizeStatus(selected)}</dd></div><div><dt>Deadline</dt><dd className={`deadline-text ${deadlineTone(selected)}`}>{formatDate(selected.dueDate)}</dd></div><div><dt>Responsable</dt><dd>{selected.assignedToName || 'Non affectée'}</dd></div><div><dt>Source</dt><dd>{selected.sourceType || 'Manuelle'}</dd></div></dl></section>
          <section className="detail-card"><h3>Description</h3><div className="task-description">{selected.description || selected.smsMessage || 'Aucune description.'}</div>{(selected.description || selected.smsMessage) && <button className="button button-secondary task-copy" onClick={()=>copyDescription(selected)}><Clipboard size={15}/> Copier le contenu</button>}</section>
          {selected.parentName && <section className="detail-card"><h3>Contexte parent</h3><dl className="detail-grid"><div><dt>Parent</dt><dd>{selected.parentName}</dd></div><div><dt>Téléphone</dt><dd>{selected.phone || '—'}</dd></div>{selected.username && <><div><dt>Nom d’utilisateur</dt><dd>{selected.username}</dd></div><div><dt>Code temporaire</dt><dd>{selected.temporaryCode || '••••••'}</dd></div></>}</dl></section>}
          {canManage && <section className="detail-card"><h3>Changer le statut</h3><div className="task-status-actions"><button className="button button-secondary" disabled={saving} onClick={()=>setStatus(selected,'open')}>Ouverte</button><button className="button status-waiting-button" disabled={saving} onClick={()=>setStatus(selected,'waiting')}>En attente</button><button className="button status-blocked-button" disabled={saving} onClick={()=>setStatus(selected,'blocked')}><AlertTriangle size={15}/> Bloquée</button><button className="button action-success" disabled={saving} onClick={()=>setStatus(selected,'done')}><CheckCircle2 size={15}/> Terminée</button></div></section>}
        </div>
      </aside>
    </div>}
  </>;
}
